import React from 'react';
import { Box, Checkbox, FormControlLabel, Typography } from '@mui/material';
import type { NotificationFESchemaDTO } from 'axiosApi/models/notification-feschema-dto';
import intl from 'components/i18n/ReactIntlWrapper';

interface ActionCheckboxItemProps {
  actionName: string;
  schema: NotificationFESchemaDTO | null | undefined;
  isChecked: boolean;
  onCheck: (checked: boolean) => void;
  onClick: () => void;
}

/**
 * ActionCheckboxItem - Renders a single checkbox item for a notification action
 * 
 * This component displays:
 * - Action name (e.g., "workflow.create")
 * - Optional subtitle from schema.message.resourceID
 * - Checkbox for selection state
 * 
 * Behavior:
 * - Clicking the checkbox only checks/unchecks it
 * - Clicking the action name opens the configuration drawer
 */
const ActionCheckboxItem: React.FC<ActionCheckboxItemProps> = ({
  actionName,
  schema,
  isChecked,
  onCheck,
  onClick,
}) => {
  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onCheck(event.target.checked);
  };

  const handleActionNameClick = (event: React.MouseEvent) => {
    event.preventDefault(); // Prevent FormControlLabel from toggling checkbox
    event.stopPropagation(); // Prevent any parent handlers
    onClick();
  };

  return (
    <Box
      sx={{
        py: 1,
        px: 2,
        borderRadius: 1,
        transition: 'background-color 0.2s',
        '&:hover': {
          bgcolor: 'action.hover',
        },
      }}
    >
      <FormControlLabel
        control={
          <Checkbox
            checked={isChecked}
            onChange={handleCheckboxChange}
            color="success"
          />
        }
        label={
          <Box onClick={handleActionNameClick} sx={{ cursor: 'pointer', flex: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {intl.get(`${actionName}`)}
            </Typography>
          </Box>
        }
        sx={{ m: 0, width: '100%' }}
      />
    </Box>
  );
};

export default ActionCheckboxItem;
